import type { DocumentPickerOptions } from 'expo-document-picker';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useUploadTemporaryDocumentItem } from '@/features/api/hooks/shared-document/useUploadTemporaryDocumentItem';
import { useUploadTemporaryMediaItem } from '@/features/api/hooks/shared-media/useUploadTemporaryMediaItem';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import type { AttachmentFeature } from '@/features/feature-management/types/attachmentFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import type {
  ImagePickerResult,
  ImagePickType
} from '@/features/media/types/picker';
import { pickImages, pickDocuments } from '@/features/media/utils/picker';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

export const useMediaPicker = () => {
  const { t } = useTranslation(LocalizationNameSpace.MEDIA, {
    keyPrefix: 'components.media-menu'
  });

  const {
    mutateAsync: uploadTemporaryMediaItem,
    isPending: isImageUploadPending
  } = useUploadTemporaryMediaItem();
  const {
    mutateAsync: uploadTemporaryDocumentItem,
    isPending: isDocumentUploadPending
  } = useUploadTemporaryDocumentItem();

  const { showAlert } = usePopup();
  const { getFeature } = useFeature();

  const pickImage = useCallback(
    async (pick: ImagePickType) => {
      const images = await pickImages(pick);
      const image = images?.[0];
      if (!image) return;

      try {
        const temporaryMediaItem = await uploadTemporaryMediaItem(image);
        return { image, temporaryMediaItem };
      } catch {
        // Upload failed (e.g. image stays above the size limit even after
        // compression). Still return the picked image so callers can add it
        // to the form and let validation surface the problem.
        return { image };
      }
    },
    [uploadTemporaryMediaItem]
  );

  const pickMultipleImages = useCallback(
    async (pick: ImagePickType, selectionLimit?: number) => {
      const images = await pickImages(pick, {
        allowsMultipleSelection: true,
        selectionLimit
      });
      if (!images) return;

      const results: ImagePickerResult[] = [];
      for (const image of images) {
        try {
          const temporaryMediaItem = await uploadTemporaryMediaItem(image);
          results.push({ image, temporaryMediaItem });
        } catch {
          // Keep images that fail to upload — form validation flags them.
          results.push({ image });
        }
      }
      return results;
    },
    [uploadTemporaryMediaItem]
  );

  const { featureValues: attachmentFeatureValues } =
    getFeature<AttachmentFeature>(FeaturesEnum.Attachments);
  const { maxFileSizeKb, allowedMediaTypes } = attachmentFeatureValues;

  const pickDocument = useCallback(
    async (options?: DocumentPickerOptions) => {
      const documents = await pickDocuments({
        ...options,
        type: allowedMediaTypes
      });

      const document = documents?.[0];
      if (!document) return;

      if (document.size != undefined && document.size > maxFileSizeKb * 1024) {
        showAlert({
          labels: {
            title: t('alerts.document.size.title'),
            content: t('alerts.document.size.content', {
              maxFileSizeKb
            }),
            confirm: t('alerts.document.size.confirm')
          }
        });
      } else {
        try {
          const temporaryDocumentItem =
            await uploadTemporaryDocumentItem(document);
          return { document, temporaryDocumentItem };
        } catch {
          return;
        }
      }
    },
    [
      allowedMediaTypes,
      maxFileSizeKb,
      showAlert,
      t,
      uploadTemporaryDocumentItem
    ]
  );

  return {
    pickImage,
    pickMultipleImages,
    isImageUploadPending,
    pickDocument,
    isDocumentUploadPending,
    attachmentFeatureValues
  };
};
